<template>
  <v-container fluid class="pa-2">
    <v-card outlined class="compact-card">
      <v-card-title class="pa-3 text-subtitle-2 font-weight-bold">
        Create New Report
      </v-card-title>
      <v-card-text class="pa-3">
        <v-form ref="reportForm">
          <v-row dense v-if="showDateRange">
            <v-col cols="12" sm="6">
              <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field 
                    v-model="report.parameters.startDate" 
                    label="Start Date" 
                    prepend-icon="mdi-calendar"
                    readonly outlined dense 
                    hide-details
                    class="small-text-field"
                    v-bind="attrs" 
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="report.parameters.startDate" @input="startDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6">
              <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field 
                    v-model="report.parameters.endDate" 
                    label="End Date" 
                    prepend-icon="mdi-calendar"
                    readonly outlined dense 
                    hide-details
                    class="small-text-field"
                    v-bind="attrs" 
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="report.parameters.endDate" @input="endDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12">
              <v-btn 
                color="primary" 
                small
                @click="generatePreview" 
                :loading="isGeneratingPreview" 
                block
                class="caption"
              >
                <v-icon left x-small>mdi-eye</v-icon>
                Generate Preview
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <div v-if="previewData" class="mt-4">
          <v-card outlined class="compact-table-card">
            <v-card-title class="d-flex justify-space-between align-center pa-3">
              <span class="caption font-weight-bold">Preview: {{ report.title }}</span>
              <v-chip small color="primary" class="caption">{{ report.type }}</v-chip>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text style="max-height: 450px; overflow-y: auto; padding: 8px;">
              <template v-if="report.type === 'Property Status Report'">
                <div class="table-responsive">
                  <table class="property-status-table compact-table">
                    <thead>
                      <tr>
                        <th class="caption font-weight-bold">Ref</th>
                        <th class="caption font-weight-bold">Property</th>
                        <th class="caption font-weight-bold">Address</th>
                        <th class="caption font-weight-bold">Category</th>
                        <th class="caption font-weight-bold">Type</th>
                        <th class="caption font-weight-bold">Landlord</th>
                        <th class="caption font-weight-bold text-right">Units</th>
                        <th class="caption font-weight-bold text-right">Occ</th>
                        <th class="caption font-weight-bold text-right">Vac</th>
                        <th class="caption font-weight-bold text-right">Rate</th>
                        <th class="caption font-weight-bold">Status</th>
                        <th class="caption font-weight-bold">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in previewData" :key="item.id">
                        <td class="caption">{{ item.reference }}</td>
                        <td class="caption">{{ item.displayName }}</td>
                        <td class="caption">{{ item.address }}</td>
                        <td class="caption">{{ item.category }}</td>
                        <td class="caption">{{ item.type }}</td>
                        <td class="caption">{{ item.landlord }}</td>
                        <td class="caption text-right">{{ item.totalUnits }}</td>
                        <td class="caption text-right">{{ item.occupiedUnits }}</td>
                        <td class="caption text-right">{{ item.vacantUnits }}</td>
                        <td class="caption text-right">{{ item.vacancyRate }}</td>
                        <td>
                          <v-chip small :color="item.isVacant ? 'error' : 'success'" class="caption">
                            {{ item.isVacant ? 'Vacant' : 'Occupied' }}
                          </v-chip>
                        </td>
                        <td class="caption">{{ formatDate(item.lastUpdated) }}</td>
                      </tr>
                      <tr class="totals-row">
                        <td colspan="6" class="caption text-right font-weight-bold">Totals:</td>
                        <td class="caption text-right font-weight-bold">{{ sumField('totalUnits') }}</td>
                        <td class="caption text-right font-weight-bold">{{ sumField('occupiedUnits') }}</td>
                        <td class="caption text-right font-weight-bold">{{ sumField('vacantUnits') }}</td>
                        <td class="caption text-right font-weight-bold">
                          {{ (previewData.length > 0 ? (sumField('vacantUnits') / sumField('totalUnits') * 100).toFixed(2) : 0) }}%
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </v-card-text>

            <v-card-actions class="justify-end pa-2" style="gap: 6px;">
              <v-btn 
                color="success" 
                small
                @click="report.type === 'Rent Roll' ? exportReport('Excel') : exportPropertyStatusReport('Excel')" 
                :loading="isExportingExcel"
                class="caption"
              >
                <v-icon left x-small>mdi-file-excel-box</v-icon> Excel
              </v-btn>

              <v-btn 
                color="success" 
                small
                @click="report.type === 'Rent Roll' ? generateReportPDF() : exportPropertyStatusReport('PDF')" 
                :loading="isExportingPDF"
                class="caption"
              >
                <v-icon left x-small>mdi-file-pdf-box</v-icon> PDF
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { collection, addDoc, getDocs, query, where, orderBy, deleteDoc, doc, updateDoc, getDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;
const db = getFirestore();
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';
import { saveAs } from 'file-saver';

export default {
  name: "ReportsView",
  props: {
    id: String,
    state: String,
    collection: String
  },
  data() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    const firstDayOfMonth = new Date(year, month, 1).toISOString().split('T')[0];
    const lastDayOfMonth = new Date(year, month + 1, 0).toISOString().split('T')[0];

    return {
      propertyName: this.state || 'Unknown Property',
      propertyId: this.id || '',
      
      report: {
        title: `${this.state || 'Unknown State'} - ${firstDayOfMonth}`,
        type: "Property Status Report",
        parameters: {
          startDate: firstDayOfMonth,
          endDate: lastDayOfMonth,
          includeDetails: false,
        },
      },
      showDateRange: true,
      startDateMenu: false,
      endDateMenu: false,
      isGeneratingPreview: false,
      previewData: null,
      isExportingPDF: false,
      isExportingExcel: false,
      base64log: "",
    };
  },

  async created() {
    try {
      await this.fetchLogoBase64();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  },

  methods: {
    async fetchLogoBase64() {
      const logosRef = collection(db, 'Logo');
      const q = query(logosRef, where('id', '==', 2));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const logoDoc = querySnapshot.docs[0];
        const logoData = logoDoc.data();
        this.base64log = logoData.logo2;
      } else {
        console.error('Logo not found');
        return null;
      }
    },

    async calculateTotalDue(leaseReference) {
      try {
        const currentDate = new Date();
        const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        const previousMonthKey = `${previousMonth.getFullYear()}-${String(previousMonth.getMonth() + 1).padStart(2, '0')}`;

        const balanceQuery = query(
          collection(db, "OpeningBalances"),
          where("leaseReference", "==", leaseReference),
          where("month", "==", previousMonthKey)
        );
        const balanceSnapshot = await getDocs(balanceQuery);

        let previousBalance = 0;
        if (!balanceSnapshot.empty) {
          previousBalance = parseFloat(balanceSnapshot.docs[0].data().openingBalance) || 0;
        }

        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        const transactionQuery = query(
          collection(db, "Transactions"),
          where("leaseReference", "==", leaseReference)
        );
        const transactionSnapshot = await getDocs(transactionQuery);

        let totalTransactionAmount = 0;
        transactionSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const datePaid = new Date(data.datePaid);
          if (datePaid >= startOfMonth && datePaid <= endOfMonth) {
            totalTransactionAmount += parseFloat(data.amountPaid) || 0;
          }
        });

        const recurringChargesQuery = query(
          collection(db, "ReccuringCharges"),
          where("leaseReference", "==", leaseReference)
        );
        const recurringChargesSnapshot = await getDocs(recurringChargesQuery);

        let totalRecurringCharges = 0;
        recurringChargesSnapshot.docs.forEach((doc) => {
          totalRecurringCharges += parseFloat(doc.data().amount) || 0;
        });

        const leaseQuery = query(
          collection(db, "Leases"),
          where("leaseReference", "==", leaseReference)
        );
        const leaseSnapshot = await getDocs(leaseQuery);
        let rentAmount = 0;
        if (!leaseSnapshot.empty) {
          rentAmount = parseFloat(leaseSnapshot.docs[0].data().leaseDetails.rentalAmount) || 0;
        }

        return previousBalance + rentAmount + totalTransactionAmount + totalRecurringCharges;
      } catch (error) {
        console.error("Error calculating total due:", error);
        return 0;
      }
    },

    updateFormFields() {
      this.showDateRange = ['Rent Roll'].includes(this.report.type);
    },

    async generatePreview() {
      const isValid = await this.$refs.reportForm.validate();
      if (!isValid) return;

      if (new Date(this.report.parameters.endDate) < new Date(this.report.parameters.startDate)) {
        this.$toast.error("End date cannot be before start date");
        return;
      }

      this.isGeneratingPreview = true;

      try {
        if (this.report.type === 'Rent Roll') {
          this.previewData = await this.generateRentRollData();
        } else if (this.report.type === 'Property Status Report') {
          this.previewData = await this.generatePropertyStatusData();
        }
      } catch (error) {
        console.error("Error generating report:", error);
        this.$toast.error("Failed to generate report preview");
      } finally {
        this.isGeneratingPreview = false;
      }
    },

    async generatePropertyStatusData() {
      try {
        const querySnapshot = await getDocs(collection(db, "properties"));
        const properties = await Promise.all(querySnapshot.docs.map(async (doc) => {
          const data = doc.data();

          let units = [];
          try {
            const unitsSnapshot = await getDocs(collection(db, "properties", doc.id, "units"));
            units = unitsSnapshot.docs.map(unitDoc => unitDoc.data());
          } catch (error) {
            console.error(`Error fetching units for property ${doc.id}:`, error);
          }

          const occupiedUnits = units.filter(unit => unit.isOccupied).length;
          const totalUnits = units.length;
          const vacancyRate = totalUnits > 0 ? ((totalUnits - occupiedUnits) / totalUnits * 100).toFixed(2) : 0;

          let landlordName = 'N/A';
          if (data.selectedLandlord) {
            try {
              const landlordDoc = await getDoc(doc(db, "landlords", data.selectedLandlord));
              if (landlordDoc.exists()) {
                landlordName = landlordDoc.data().displayName || 'N/A';
              }
            } catch (error) {
              console.error(`Error fetching landlord for property ${doc.id}:`, error);
            }
          }

          return {
            id: doc.id,
            reference: data.reference,
            displayName: data.displayName,
            address: `${data.address.building} ${data.address.street}, ${data.address.suburb}, ${data.address.city}`,
            category: data.category,
            type: data.type,
            landlord: landlordName,
            totalUnits: totalUnits,
            occupiedUnits: occupiedUnits,
            vacantUnits: totalUnits - occupiedUnits,
            vacancyRate: vacancyRate + '%',
            isVacant: totalUnits > 0 ? occupiedUnits < totalUnits : data.isVacant,
            lastUpdated: new Date().toISOString().split('T')[0]
          };
        }));

        return properties.sort((a, b) => a.reference.localeCompare(b.reference));
      } catch (error) {
        console.error("Error generating property status report:", error);
        this.$toast.error("Failed to generate property status report");
        return [];
      }
    },

    async generateRentRollData() {
      try {
        const leasesQuery = query(
          collection(db, "Leases"),
          where("propertyId", "==", this.propertyId)
        );
        const leasesSnapshot = await getDocs(leasesQuery);

        const reportData = [];

        for (const leaseDoc of leasesSnapshot.docs) {
          const leaseData = leaseDoc.data();
          const calculatedOnlyRealty = await this.calculateTotalDue(leaseData.leaseReference);

          const item = {
            reference: leaseData.leaseReference,
            property: this.propertyName,
            onlyRealty: calculatedOnlyRealty,
            rentCharged: parseFloat(leaseData.leaseDetails.rentalAmount) || 0,
            rentPaid: 0,
            waterCharged: 0,
            waterAv: 0,
            sewerageAv: 0,
            sewerageCharged: 0,
            refuseCharged: 0,
            admin: 0,
            parking: 0,
            bankCharges: 0,
            leaseFees: 0,
            bookkeeping: 0,
            collectionFee: 0,
            insurance: 0,
            landscaping: 0,
            legalExpenses: 0,
            repairsMaintenance: 0,
            utilities: 0,
            otherVendorExpenses: 0,
            otherCharge: 0,
            subTotal: 0,
            paymentDate: '',
            bankReceived: 0,
            balance: 0,
            arrearsCollected: 0
          };

          const transactionsQuery = query(
            collection(db, "Transactions"),
            where("leaseReference", "==", leaseData.leaseReference),
            where("datePaid", ">=", this.report.parameters.startDate),
            where("datePaid", "<=", this.report.parameters.endDate)
          );
          const transactionsSnapshot = await getDocs(transactionsQuery);

          const recurringChargesQuery = query(
            collection(db, "ReccuringCharges"),
            where("leaseReference", "==", leaseData.leaseReference)
          );
          const recurringChargesSnapshot = await getDocs(recurringChargesQuery);

          recurringChargesSnapshot.forEach(chargeDoc => {
            const chargeData = chargeDoc.data();
            const amount = parseFloat(chargeData.amount) || 0;
            const description = chargeData.transactionType ? chargeData.transactionType.toLowerCase() : '';

            if (description.includes('water') && !description.includes('av')) {
              item.waterCharged += amount;
            } else if (description.includes('sewerage') && !description.includes('av')) {
              item.sewerageCharged += amount;
            } else if (description.includes('water av')) {
              item.waterAv = 'Yes';
            } else if (description.includes('sewerage av')) {
              item.sewerageAv = 'Yes';
            } else if (description.includes('refuse')) {
              item.refuseCharged += amount;
            } else if (description.includes('admin')) {
              item.admin += amount;
            } else if (description.includes('parking')) {
              item.parking += amount;
            } else if (description.includes('bank')) {
              item.bankCharges += amount;
            } else if (description.includes('lease')) {
              item.leaseFees += amount;
            } else if (description.includes('bookkeeping')) {
              item.bookkeeping += amount;
            } else if (description.includes('collection fee')) {
              item.collectionFee += amount;
            } else if (description.includes('insurance')) {
              item.insurance += amount;
            } else if (description.includes('landscaping')) {
              item.landscaping += amount;
            } else if (description.includes('legal')) {
              item.legalExpenses += amount;
            } else if (description.includes('repair') || description.includes('maintenance')) {
              item.repairsMaintenance += amount;
            } else if (description.includes('utilities')) {
              item.utilities += amount;
            } else if (description.includes('vendor')) {
              item.otherVendorExpenses += amount;
            } else {
              item.otherCharge += amount;
            }
          });

          let totalPayments = 0;
          let latestPaymentDate = null;
          let paymentDates = [];

          for (const transDoc of transactionsSnapshot.docs) {
            const transData = transDoc.data();

            if (transData.type === 'payment') {
              const amount = Math.abs(parseFloat(transData.amountPaid)) || 0;
              totalPayments += amount;

              if (transData.datePaid) {
                paymentDates.push(transData.datePaid);
                if (!latestPaymentDate || transData.datePaid > latestPaymentDate) {
                  latestPaymentDate = transData.datePaid;
                }
              }

              const allocationQuery = query(
                collection(db, "PaymentAllocations"),
                where("paymentId", "==", transDoc.id)
              );
              const allocationSnapshot = await getDocs(allocationQuery);

              if (!allocationSnapshot.empty) {
                const allocationData = allocationSnapshot.docs[0].data();

                for (const allocItem of allocationData.items) {
                  const allocAmount = parseFloat(allocItem.amount) || 0;
                  const allocDesc = allocItem.description.toLowerCase();

                  if (allocDesc.includes('rent')) {
                    item.rentPaid += allocAmount;
                  } else if (allocDesc.includes('water') && !allocDesc.includes('av')) {
                    item.waterCharged -= allocAmount;
                  } else if (allocDesc.includes('sewerage') && !allocDesc.includes('av')) {
                    item.sewerageCharged -= allocAmount;
                  } else if (allocDesc.includes('refuse')) {
                    item.refuseCharged -= allocAmount;
                  } else if (allocDesc.includes('admin')) {
                    item.admin -= allocAmount;
                  } else if (allocDesc.includes('parking')) {
                    item.parking -= allocAmount;
                  } else if (allocDesc.includes('bank')) {
                    item.bankCharges -= allocAmount;
                  } else if (allocDesc.includes('lease')) {
                    item.leaseFees -= allocAmount;
                  } else if (allocDesc.includes('bookkeeping')) {
                    item.bookkeeping -= allocAmount;
                  } else if (allocDesc.includes('collection fee')) {
                    item.collectionFee -= allocAmount;
                  } else if (allocDesc.includes('insurance')) {
                    item.insurance -= allocAmount;
                  } else if (allocDesc.includes('landscaping')) {
                    item.landscaping -= allocAmount;
                  } else if (allocDesc.includes('legal')) {
                    item.legalExpenses -= allocAmount;
                  } else if (allocDesc.includes('repair') || allocDesc.includes('maintenance')) {
                    item.repairsMaintenance -= allocAmount;
                  } else if (allocDesc.includes('utilities')) {
                    item.utilities -= allocAmount;
                  } else if (allocDesc.includes('vendor')) {
                    item.otherVendorExpenses -= allocAmount;
                  } else {
                    item.otherCharge -= allocAmount;
                  }
                }
              } else {
                item.rentPaid += amount;
              }
            }
          }

          item.bankReceived = totalPayments;

          if (paymentDates.length > 0) {
            if (paymentDates.length === 1) {
              item.paymentDate = paymentDates[0];
            } else {
              item.paymentDate = `${paymentDates.length} payments (${paymentDates.join(', ')})`;
            }
          }

          item.subTotal = item.rentCharged +
            item.waterCharged +
            item.sewerageCharged +
            item.refuseCharged +
            item.admin +
            item.parking +
            item.bankCharges +
            item.leaseFees +
            item.bookkeeping +
            item.collectionFee +
            item.insurance +
            item.landscaping +
            item.legalExpenses +
            item.repairsMaintenance +
            item.utilities +
            item.otherVendorExpenses +
            item.otherCharge;

          item.balance = item.subTotal - item.rentPaid;

          reportData.push(item);
        }

        reportData.sort((a, b) => a.reference.localeCompare(b.reference));
        return reportData;

      } catch (error) {
        console.error("Error generating rent roll data:", error);
        this.$toast.error("Failed to generate rent roll data");
        return [];
      }
    },

    showColumn(field) {
      if (field === 'reference' || field === 'property' ||
        field === 'subTotal' || field === 'paymentDate' ||
        field === 'bankReceived' || field === 'balance') {
        return true;
      }

      return this.previewData.some(item => {
        const value = item[field];
        return value !== undefined && value !== null && value !== 0;
      });
    },

    formatCurrency(value) {
      if (value === null || value === undefined || value === '' || isNaN(value)) {
        return '';
      }
      return 'R ' + new Intl.NumberFormat('en-ZA', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    formatDate(date) {
      if (!date || isNaN(new Date(date).getTime())) {
        return '';
      }
      return new Date(date).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    sumField(field) {
      return this.previewData.reduce((sum, item) => {
        const value = item[field];
        if (value === null || value === undefined || isNaN(value)) {
          return sum;
        }
        return sum + value;
      }, 0);
    },

    async exportPropertyStatusReport(format) {
      if (format === 'Excel') {
        await this.exportPropertyStatusExcel();
      } else if (format === 'PDF') {
        await this.generatePropertyStatusPDF();
      }
    },

    async exportPropertyStatusExcel() {
      this.isExportingExcel = true;
      
      try {
        const headers = [
          'Reference', 'Property Name', 'Address', 'Category', 'Type', 'Landlord',
          'Total Units', 'Occupied', 'Vacant', 'Vacancy Rate', 'Status', 'Last Updated'
        ];

        const data = this.previewData.map(item => ({
          'Reference': item.reference,
          'Property Name': item.displayName,
          'Address': item.address,
          'Category': item.category,
          'Type': item.type,
          'Landlord': item.landlord,
          'Total Units': item.totalUnits,
          'Occupied': item.occupiedUnits,
          'Vacant': item.vacantUnits,
          'Vacancy Rate': item.vacancyRate,
          'Status': item.isVacant ? 'Vacant' : 'Occupied',
          'Last Updated': item.lastUpdated
        }));

        data.push({
          'Reference': 'TOTALS',
          'Property Name': '',
          'Address': '',
          'Category': '',
          'Type': '',
          'Landlord': '',
          'Total Units': this.sumField('totalUnits'),
          'Occupied': this.sumField('occupiedUnits'),
          'Vacant': this.sumField('vacantUnits'),
          'Vacancy Rate': (this.sumField('vacantUnits') / this.sumField('totalUnits') * 100 + '%'),
          'Status': '',
          'Last Updated': ''
        });

        const ws = XLSXUtils.json_to_sheet(data, { header: headers });

        if (!ws['!cols']) ws['!cols'] = [];
        headers.forEach((header, i) => {
          ws['!cols'][i] = { width: 16 };
        });

        const wb = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(wb, ws, "Property Status");

        const fileName = `${this.report.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSXWriteFile(wb, fileName);

        this.$toast.success("Property Status Report exported to Excel!");
      } catch (error) {
        console.error("Error exporting Excel:", error);
        this.$toast.error("Failed to export Excel");
      } finally {
        this.isExportingExcel = false;
      }
    },

    async generatePropertyStatusPDF() {
      this.isExportingPDF = true;

      try {
        const fonts = {
          Roboto: {
            normal: 'assets/Roboto/static/Roboto-Regular.ttf',
            bold: 'assets/Roboto/static/Roboto-Bold.ttf',
            italics: 'assets/Roboto/static/Roboto-Italic.ttf',
            bolditalics: 'assets/Roboto/static/Roboto-BoldItalic.ttf'
          }
        };

        pdfMake.fonts = fonts;

        const columnHeaders = [
          { text: 'Ref', style: 'tableHeader' },
          { text: 'Property', style: 'tableHeader' },
          { text: 'Address', style: 'tableHeader' },
          { text: 'Category', style: 'tableHeader' },
          { text: 'Type', style: 'tableHeader' },
          { text: 'Landlord', style: 'tableHeader' },
          { text: 'Units', style: 'tableHeader' },
          { text: 'Occ', style: 'tableHeader' },
          { text: 'Vac', style: 'tableHeader' },
          { text: 'Rate', style: 'tableHeader' },
          { text: 'Status', style: 'tableHeader' },
          { text: 'Updated', style: 'tableHeader' }
        ];

        const tableBody = [columnHeaders];

        this.previewData.forEach(item => {
          tableBody.push([
            { text: item.reference, style: 'tableData' },
            { text: item.displayName, style: 'tableData' },
            { text: item.address, style: 'tableData' },
            { text: item.category, style: 'tableData' },
            { text: item.type, style: 'tableData' },
            { text: item.landlord, style: 'tableData' },
            { text: item.totalUnits, style: 'tableData', alignment: 'right' },
            { text: item.occupiedUnits, style: 'tableData', alignment: 'right' },
            { text: item.vacantUnits, style: 'tableData', alignment: 'right' },
            { text: item.vacancyRate, style: 'tableData', alignment: 'right' },
            { text: item.isVacant ? 'Vacant' : 'Occupied', style: 'tableData' },
            { text: item.lastUpdated, style: 'tableData' }
          ]);
        });

        const totalUnits = this.sumField('totalUnits');
        const occupiedUnits = this.sumField('occupiedUnits');
        const vacantUnits = this.sumField('vacantUnits');
        const vacancyRate = totalUnits > 0 ? `${((vacantUnits / totalUnits) * 100).toFixed(2)}%` : '0%';

        tableBody.push([
          { text: 'TOTALS', style: 'tableFooter', colSpan: 6 },
          { text: '', style: 'tableFooter' },
          { text: '', style: 'tableFooter' },
          { text: '', style: 'tableFooter' },
          { text: '', style: 'tableFooter' },
          { text: '', style: 'tableFooter' },
          { text: totalUnits, style: 'tableFooter', alignment: 'right' },
          { text: occupiedUnits, style: 'tableFooter', alignment: 'right' },
          { text: vacantUnits, style: 'tableFooter', alignment: 'right' },
          { text: vacancyRate, style: 'tableFooter', alignment: 'right' },
          { text: '', style: 'tableFooter' },
          { text: '', style: 'tableFooter' }
        ]);

        const docDefinition = {
          defaultStyle: { font: 'Roboto' },
          content: [
            {
              image: this.base64log,
              width: 100,
              alignment: 'center',
              margin: [0, 0, 0, 8]
            },
            {
              text: this.report.title.toUpperCase(),
              style: 'header',
              alignment: 'center',
              margin: [0, 0, 0, 10]
            },
            {
              columns: [
                { text: `Report: ${this.report.type}`, style: 'propertyInfo' },
                { text: `Generated: ${new Date().toLocaleDateString()}`, style: 'propertyInfo', alignment: 'right' }
              ],
              margin: [0, 0, 0, 15]
            },
            {
              table: {
                headerRows: 1,
                widths: ['8%', '12%', '*', '10%', '10%', '10%', '8%', '8%', '8%', '8%', '10%', '10%'],
                body: tableBody
              },
              layout: {
                fillColor: function (rowIndex) {
                  return (rowIndex === 0) ? '#F1C284' : null;
                },
                hLineColor: function () { return '#CCCCCC'; },
                vLineColor: function () { return '#CCCCCC'; }
              },
              margin: [0, 0, 0, 15]
            },
            {
              text: 'Summary',
              style: 'sectionHeader',
              margin: [0, 0, 0, 8]
            },
            {
              table: {
                widths: ['*', '*'],
                body: [
                  [{ text: 'Total Properties', style: 'summaryLabel' }, { text: this.previewData.length.toString(), style: 'summaryValue' }],
                  [{ text: 'Total Units', style: 'summaryLabel' }, { text: totalUnits.toString(), style: 'summaryValue' }],
                  [{ text: 'Occupied Units', style: 'summaryLabel' }, { text: occupiedUnits.toString(), style: 'summaryValue' }],
                  [{ text: 'Vacant Units', style: 'summaryLabel' }, { text: vacantUnits.toString(), style: 'summaryValue' }],
                  [{ text: 'Vacancy Rate', style: 'summaryLabel' }, { text: vacancyRate, style: 'summaryValue' }]
                ]
              },
              layout: 'noBorders'
            }
          ],
          footer: function (currentPage, pageCount) {
            return {
              stack: [
                {
                  canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#000000' }],
                  margin: [0, 0, 0, 3]
                },
                {
                  text: `Page ${currentPage} of ${pageCount}`,
                  fontSize: 7,
                  alignment: 'right',
                  margin: [0, 3, 10, 0]
                }
              ]
            };
          },
          styles: {
            header: { fontSize: 14, bold: true, color: '#000080' },
            propertyInfo: { fontSize: 8 },
            sectionHeader: { fontSize: 11, bold: true, color: '#000080', margin: [0, 0, 0, 8] },
            tableHeader: { bold: true, fontSize: 7.5, color: '#FFFFFF', alignment: 'center' },
            tableData: { fontSize: 7, alignment: 'center' },
            tableFooter: { fontSize: 7, bold: true, fillColor: '#E0E0E0', alignment: 'center' },
            summaryLabel: { fontSize: 8, bold: true },
            summaryValue: { fontSize: 8, bold: true, alignment: 'right' }
          },
          pageMargins: [30, 40, 30, 40],
          pageSize: 'A4',
          pageOrientation: 'landscape'
        };

        pdfMake.createPdf(docDefinition).open();
      } catch (error) {
        console.error("Error generating Property Status PDF:", error);
        this.$toast.error("Failed to generate PDF");
      } finally {
        this.isExportingPDF = false;
      }
    },

    generateExcel() {
      return new Promise((resolve) => {
        const headers = ['Reference', 'Property'];
        if (this.showColumn('onlyRealty')) headers.push('Only Realty');
        if (this.showColumn('rentCharged')) headers.push('Rent Charged');
        if (this.showColumn('rentPaid')) headers.push('Rent Paid');
        if (this.showColumn('waterCharged')) headers.push('Water Charged');
        if (this.showColumn('waterAv')) headers.push('Water Av');
        if (this.showColumn('sewerageCharged')) headers.push('Sewerage Charged');
        if (this.showColumn('sewerageAv')) headers.push('Sewerage Av');
        if (this.showColumn('refuseCharged')) headers.push('Refuse Charged');
        if (this.showColumn('admin')) headers.push('Admin');
        if (this.showColumn('parking')) headers.push('Parking');
        if (this.showColumn('bankCharges')) headers.push('Bank Charges');
        if (this.showColumn('leaseFees')) headers.push('Lease Fees');
        if (this.showColumn('bookkeeping')) headers.push('Bookkeeping');
        if (this.showColumn('collectionFee')) headers.push('Collection Fee');
        if (this.showColumn('insurance')) headers.push('Insurance');
        if (this.showColumn('landscaping')) headers.push('Landscaping');
        if (this.showColumn('legalExpenses')) headers.push('Legal Expenses');
        if (this.showColumn('repairsMaintenance')) headers.push('Repairs & Maint');
        if (this.showColumn('utilities')) headers.push('Utilities');
        if (this.showColumn('otherVendorExpenses')) headers.push('Other Vendor Exp');
        if (this.showColumn('otherCharge')) headers.push('Other Charge');
        headers.push('Sub Total', 'Payment Date', 'Bank Received', 'Balance');
        if (this.showColumn('arrearsCollected')) headers.push('Arrears Collected');

        const data = this.previewData.map(item => {
          const row = { 'Reference': item.reference, 'Property': item.property };
          if (this.showColumn('onlyRealty')) row['Only Realty'] = this.formatCurrency(item.onlyRealty);
          if (this.showColumn('rentCharged')) row['Rent Charged'] = this.formatCurrency(item.rentCharged);
          if (this.showColumn('rentPaid')) row['Rent Paid'] = this.formatCurrency(item.rentPaid);
          if (this.showColumn('waterCharged')) row['Water Charged'] = this.formatCurrency(item.waterCharged);
          if (this.showColumn('waterAv')) row['Water Av'] = item.waterAv;
          if (this.showColumn('sewerageCharged')) row['Sewerage Charged'] = this.formatCurrency(item.sewerageCharged);
          if (this.showColumn('sewerageAv')) row['Sewerage Av'] = item.sewerageAv;
          if (this.showColumn('refuseCharged')) row['Refuse Charged'] = this.formatCurrency(item.refuseCharged);
          if (this.showColumn('admin')) row['Admin'] = this.formatCurrency(item.admin);
          if (this.showColumn('parking')) row['Parking'] = this.formatCurrency(item.parking);
          if (this.showColumn('bankCharges')) row['Bank Charges'] = this.formatCurrency(item.bankCharges);
          if (this.showColumn('leaseFees')) row['Lease Fees'] = this.formatCurrency(item.leaseFees);
          if (this.showColumn('bookkeeping')) row['Bookkeeping'] = this.formatCurrency(item.bookkeeping);
          if (this.showColumn('collectionFee')) row['Collection Fee'] = this.formatCurrency(item.collectionFee);
          if (this.showColumn('insurance')) row['Insurance'] = this.formatCurrency(item.insurance);
          if (this.showColumn('landscaping')) row['Landscaping'] = this.formatCurrency(item.landscaping);
          if (this.showColumn('legalExpenses')) row['Legal Expenses'] = this.formatCurrency(item.legalExpenses);
          if (this.showColumn('repairsMaintenance')) row['Repairs & Maint'] = this.formatCurrency(item.repairsMaintenance);
          if (this.showColumn('utilities')) row['Utilities'] = this.formatCurrency(item.utilities);
          if (this.showColumn('otherVendorExpenses')) row['Other Vendor Exp'] = this.formatCurrency(item.otherVendorExpenses);
          if (this.showColumn('otherCharge')) row['Other Charge'] = this.formatCurrency(item.otherCharge);
          row['Sub Total'] = this.formatCurrency(item.subTotal);
          row['Payment Date'] = item.paymentDate ? this.formatDate(item.paymentDate) : '';
          row['Bank Received'] = this.formatCurrency(item.bankReceived);
          row['Balance'] = this.formatCurrency(item.balance);
          if (this.showColumn('arrearsCollected')) row['Arrears Collected'] = this.formatCurrency(item.arrearsCollected);
          return row;
        });

        const totalsRow = { 'Reference': 'TOTALS', 'Property': '' };
        if (this.showColumn('onlyRealty')) totalsRow['Only Realty'] = this.formatCurrency(this.sumField('onlyRealty'));
        if (this.showColumn('rentCharged')) totalsRow['Rent Charged'] = this.formatCurrency(this.sumField('rentCharged'));
        if (this.showColumn('rentPaid')) totalsRow['Rent Paid'] = this.formatCurrency(this.sumField('rentPaid'));
        if (this.showColumn('waterCharged')) totalsRow['Water Charged'] = this.formatCurrency(this.sumField('waterCharged'));
        if (this.showColumn('waterAv')) totalsRow['Water Av'] = '';
        if (this.showColumn('sewerageCharged')) totalsRow['Sewerage Charged'] = this.formatCurrency(this.sumField('sewerageCharged'));
        if (this.showColumn('sewerageAv')) totalsRow['Sewerage Av'] = '';
        if (this.showColumn('refuseCharged')) totalsRow['Refuse Charged'] = this.formatCurrency(this.sumField('refuseCharged'));
        if (this.showColumn('admin')) totalsRow['Admin'] = this.formatCurrency(this.sumField('admin'));
        if (this.showColumn('parking')) totalsRow['Parking'] = this.formatCurrency(this.sumField('parking'));
        if (this.showColumn('bankCharges')) totalsRow['Bank Charges'] = this.formatCurrency(this.sumField('bankCharges'));
        if (this.showColumn('leaseFees')) totalsRow['Lease Fees'] = this.formatCurrency(this.sumField('leaseFees'));
        if (this.showColumn('bookkeeping')) totalsRow['Bookkeeping'] = this.formatCurrency(this.sumField('bookkeeping'));
        if (this.showColumn('collectionFee')) totalsRow['Collection Fee'] = this.formatCurrency(this.sumField('collectionFee'));
        if (this.showColumn('insurance')) totalsRow['Insurance'] = this.formatCurrency(this.sumField('insurance'));
        if (this.showColumn('landscaping')) totalsRow['Landscaping'] = this.formatCurrency(this.sumField('landscaping'));
        if (this.showColumn('legalExpenses')) totalsRow['Legal Expenses'] = this.formatCurrency(this.sumField('legalExpenses'));
        if (this.showColumn('repairsMaintenance')) totalsRow['Repairs & Maint'] = this.formatCurrency(this.sumField('repairsMaintenance'));
        if (this.showColumn('utilities')) totalsRow['Utilities'] = this.formatCurrency(this.sumField('utilities'));
        if (this.showColumn('otherVendorExpenses')) totalsRow['Other Vendor Exp'] = this.formatCurrency(this.sumField('otherVendorExpenses'));
        if (this.showColumn('otherCharge')) totalsRow['Other Charge'] = this.formatCurrency(this.sumField('otherCharge'));
        totalsRow['Sub Total'] = this.formatCurrency(this.sumField('subTotal'));
        totalsRow['Payment Date'] = '';
        totalsRow['Bank Received'] = this.formatCurrency(this.sumField('bankReceived'));
        totalsRow['Balance'] = this.formatCurrency(this.sumField('balance'));
        if (this.showColumn('arrearsCollected')) totalsRow['Arrears Collected'] = this.formatCurrency(this.sumField('arrearsCollected'));

        data.push(totalsRow);

        const ws = XLSXUtils.json_to_sheet(data, { header: headers });

        if (!ws['!cols']) ws['!cols'] = [];
        headers.forEach((header, i) => {
          ws['!cols'][i] = { width: 16 };
        });

        const wb = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(wb, ws, "Rent Roll");

        const fileName = `${this.report.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSXWriteFile(wb, fileName);

        this.isExportingExcel = false;
        this.$toast.success("Report exported to Excel!");
        resolve();
      });
    },

    async generateReportPDF() {
      this.isExportingPDF = true;

      try {
        const fonts = {
          Roboto: {
            normal: 'assets/Roboto/static/Roboto-Regular.ttf',
            bold: 'assets/Roboto/static/Roboto-Bold.ttf',
            italics: 'assets/Roboto/static/Roboto-Italic.ttf',
            bolditalics: 'assets/Roboto/static/Roboto-BoldItalic.ttf'
          }
        };

        pdfMake.fonts = fonts;

        const columnHeaders = [
          { text: 'Ref', style: 'tableHeader' },
          { text: 'Property', style: 'tableHeader' }
        ];

        if (this.showColumn('onlyRealty')) columnHeaders.push({ text: 'Only Realty', style: 'tableHeader' });
        if (this.showColumn('rentCharged')) columnHeaders.push({ text: 'Rent', style: 'tableHeader' });
        if (this.showColumn('rentPaid')) columnHeaders.push({ text: 'Rent Paid', style: 'tableHeader' });
        if (this.showColumn('waterCharged')) columnHeaders.push({ text: 'Water', style: 'tableHeader' });
        if (this.showColumn('waterAv')) columnHeaders.push({ text: 'W Av', style: 'tableHeader' });
        if (this.showColumn('sewerageCharged')) columnHeaders.push({ text: 'Sewerage', style: 'tableHeader' });
        if (this.showColumn('sewerageAv')) columnHeaders.push({ text: 'S Av', style: 'tableHeader' });
        if (this.showColumn('refuseCharged')) columnHeaders.push({ text: 'Refuse', style: 'tableHeader' });
        if (this.showColumn('admin')) columnHeaders.push({ text: 'Admin', style: 'tableHeader' });
        if (this.showColumn('parking')) columnHeaders.push({ text: 'Parking', style: 'tableHeader' });
        if (this.showColumn('bankCharges')) columnHeaders.push({ text: 'Bank', style: 'tableHeader' });
        if (this.showColumn('leaseFees')) columnHeaders.push({ text: 'Lease Fees', style: 'tableHeader' });
        if (this.showColumn('bookkeeping')) columnHeaders.push({ text: 'Bookkeep', style: 'tableHeader' });
        if (this.showColumn('collectionFee')) columnHeaders.push({ text: 'Collection', style: 'tableHeader' });
        if (this.showColumn('insurance')) columnHeaders.push({ text: 'Insurance', style: 'tableHeader' });
        if (this.showColumn('landscaping')) columnHeaders.push({ text: 'Landscape', style: 'tableHeader' });
        if (this.showColumn('legalExpenses')) columnHeaders.push({ text: 'Legal', style: 'tableHeader' });
        if (this.showColumn('repairsMaintenance')) columnHeaders.push({ text: 'Repairs', style: 'tableHeader' });
        if (this.showColumn('utilities')) columnHeaders.push({ text: 'Utilities', style: 'tableHeader' });
        if (this.showColumn('otherVendorExpenses')) columnHeaders.push({ text: 'Vendor', style: 'tableHeader' });
        if (this.showColumn('otherCharge')) columnHeaders.push({ text: 'Other', style: 'tableHeader' });
        columnHeaders.push(
          { text: 'Sub Total', style: 'tableHeader' },
          { text: 'Payment Date', style: 'tableHeader' },
          { text: 'Bank Recv', style: 'tableHeader' },
          { text: 'Balance', style: 'tableHeader' }
        );
        if (this.showColumn('arrearsCollected')) columnHeaders.push({ text: 'Arrears', style: 'tableHeader' });

        const tableBody = [columnHeaders];

        this.previewData.forEach(item => {
          const row = [
            { text: item.reference, style: 'tableData' },
            { text: item.property, style: 'tableData' }
          ];

          if (this.showColumn('onlyRealty')) row.push({ text: this.formatCurrency(item.onlyRealty), style: 'tableData' });
          if (this.showColumn('rentCharged')) row.push({ text: this.formatCurrency(item.rentCharged), style: 'tableData' });
          if (this.showColumn('rentPaid')) row.push({ text: this.formatCurrency(item.rentPaid), style: 'tableData' });
          if (this.showColumn('waterCharged')) row.push({ text: this.formatCurrency(item.waterCharged), style: 'tableData' });
          if (this.showColumn('waterAv')) row.push({ text: item.waterAv, style: 'tableData' });
          if (this.showColumn('sewerageCharged')) row.push({ text: this.formatCurrency(item.sewerageCharged), style: 'tableData' });
          if (this.showColumn('sewerageAv')) row.push({ text: item.sewerageAv, style: 'tableData' });
          if (this.showColumn('refuseCharged')) row.push({ text: this.formatCurrency(item.refuseCharged), style: 'tableData' });
          if (this.showColumn('admin')) row.push({ text: this.formatCurrency(item.admin), style: 'tableData' });
          if (this.showColumn('parking')) row.push({ text: this.formatCurrency(item.parking), style: 'tableData' });
          if (this.showColumn('bankCharges')) row.push({ text: this.formatCurrency(item.bankCharges), style: 'tableData' });
          if (this.showColumn('leaseFees')) row.push({ text: this.formatCurrency(item.leaseFees), style: 'tableData' });
          if (this.showColumn('bookkeeping')) row.push({ text: this.formatCurrency(item.bookkeeping), style: 'tableData' });
          if (this.showColumn('collectionFee')) row.push({ text: this.formatCurrency(item.collectionFee), style: 'tableData' });
          if (this.showColumn('insurance')) row.push({ text: this.formatCurrency(item.insurance), style: 'tableData' });
          if (this.showColumn('landscaping')) row.push({ text: this.formatCurrency(item.landscaping), style: 'tableData' });
          if (this.showColumn('legalExpenses')) row.push({ text: this.formatCurrency(item.legalExpenses), style: 'tableData' });
          if (this.showColumn('repairsMaintenance')) row.push({ text: this.formatCurrency(item.repairsMaintenance), style: 'tableData' });
          if (this.showColumn('utilities')) row.push({ text: this.formatCurrency(item.utilities), style: 'tableData' });
          if (this.showColumn('otherVendorExpenses')) row.push({ text: this.formatCurrency(item.otherVendorExpenses), style: 'tableData' });
          if (this.showColumn('otherCharge')) row.push({ text: this.formatCurrency(item.otherCharge), style: 'tableData' });

          row.push(
            { text: this.formatCurrency(item.subTotal), style: 'tableData' },
            { text: item.paymentDate ? this.formatDate(item.paymentDate) : '', style: 'tableData' },
            { text: this.formatCurrency(item.bankReceived), style: 'tableData' },
            { text: this.formatCurrency(item.balance), style: 'tableData' }
          );

          if (this.showColumn('arrearsCollected')) row.push({ text: this.formatCurrency(item.arrearsCollected), style: 'tableData' });

          tableBody.push(row);
        });

        const totalsRow = [
          { text: 'TOTALS', style: 'tableFooter', colSpan: 2 },
          { text: '', style: 'tableFooter' }
        ];

        if (this.showColumn('onlyRealty')) totalsRow.push({ text: this.formatCurrency(this.sumField('onlyRealty')), style: 'tableFooter' });
        if (this.showColumn('rentCharged')) totalsRow.push({ text: this.formatCurrency(this.sumField('rentCharged')), style: 'tableFooter' });
        if (this.showColumn('rentPaid')) totalsRow.push({ text: this.formatCurrency(this.sumField('rentPaid')), style: 'tableFooter' });
        if (this.showColumn('waterCharged')) totalsRow.push({ text: this.formatCurrency(this.sumField('waterCharged')), style: 'tableFooter' });
        if (this.showColumn('waterAv')) totalsRow.push({ text: '', style: 'tableFooter' });
        if (this.showColumn('sewerageCharged')) totalsRow.push({ text: this.formatCurrency(this.sumField('sewerageCharged')), style: 'tableFooter' });
        if (this.showColumn('sewerageAv')) totalsRow.push({ text: '', style: 'tableFooter' });
        if (this.showColumn('refuseCharged')) totalsRow.push({ text: this.formatCurrency(this.sumField('refuseCharged')), style: 'tableFooter' });
        if (this.showColumn('admin')) totalsRow.push({ text: this.formatCurrency(this.sumField('admin')), style: 'tableFooter' });
        if (this.showColumn('parking')) totalsRow.push({ text: this.formatCurrency(this.sumField('parking')), style: 'tableFooter' });
        if (this.showColumn('bankCharges')) totalsRow.push({ text: this.formatCurrency(this.sumField('bankCharges')), style: 'tableFooter' });
        if (this.showColumn('leaseFees')) totalsRow.push({ text: this.formatCurrency(this.sumField('leaseFees')), style: 'tableFooter' });
        if (this.showColumn('bookkeeping')) totalsRow.push({ text: this.formatCurrency(this.sumField('bookkeeping')), style: 'tableFooter' });
        if (this.showColumn('collectionFee')) totalsRow.push({ text: this.formatCurrency(this.sumField('collectionFee')), style: 'tableFooter' });
        if (this.showColumn('insurance')) totalsRow.push({ text: this.formatCurrency(this.sumField('insurance')), style: 'tableFooter' });
        if (this.showColumn('landscaping')) totalsRow.push({ text: this.formatCurrency(this.sumField('landscaping')), style: 'tableFooter' });
        if (this.showColumn('legalExpenses')) totalsRow.push({ text: this.formatCurrency(this.sumField('legalExpenses')), style: 'tableFooter' });
        if (this.showColumn('repairsMaintenance')) totalsRow.push({ text: this.formatCurrency(this.sumField('repairsMaintenance')), style: 'tableFooter' });
        if (this.showColumn('utilities')) totalsRow.push({ text: this.formatCurrency(this.sumField('utilities')), style: 'tableFooter' });
        if (this.showColumn('otherVendorExpenses')) totalsRow.push({ text: this.formatCurrency(this.sumField('otherVendorExpenses')), style: 'tableFooter' });
        if (this.showColumn('otherCharge')) totalsRow.push({ text: this.formatCurrency(this.sumField('otherCharge')), style: 'tableFooter' });

        totalsRow.push(
          { text: this.formatCurrency(this.sumField('subTotal')), style: 'tableFooter' },
          { text: '', style: 'tableFooter' },
          { text: this.formatCurrency(this.sumField('bankReceived')), style: 'tableFooter' },
          { text: this.formatCurrency(this.sumField('balance')), style: 'tableFooter' }
        );

        if (this.showColumn('arrearsCollected')) totalsRow.push({ text: this.formatCurrency(this.sumField('arrearsCollected')), style: 'tableFooter' });

        tableBody.push(totalsRow);

        const columnCount = columnHeaders.length;
        const columnWidth = 100 / columnCount;
        const columnWidths = Array(columnCount).fill(`${columnWidth}%`);

        const docDefinition = {
          defaultStyle: { font: 'Roboto' },
          content: [
            {
              image: this.base64log,
              width: 100,
              alignment: 'center',
              margin: [0, 0, 0, 8]
            },
            {
              text: this.report.title.toUpperCase(),
              style: 'header',
              alignment: 'center',
              margin: [0, 0, 0, 10]
            },
            {
              columns: [
                { text: `Property: ${this.propertyName}`, style: 'propertyInfo' },
                { text: `Generated: ${new Date().toLocaleDateString()}`, style: 'propertyInfo', alignment: 'right' }
              ],
              margin: [0, 0, 0, 15]
            },
            {
              table: {
                headerRows: 1,
                widths: columnWidths,
                body: tableBody
              },
              layout: {
                fillColor: function (rowIndex) {
                  return (rowIndex === 0) ? '#F1C284' : null;
                },
                hLineColor: function () { return '#CCCCCC'; },
                vLineColor: function () { return '#CCCCCC'; }
              },
              margin: [0, 0, 0, 15]
            },
            {
              text: 'Summary',
              style: 'sectionHeader',
              margin: [0, 0, 0, 8]
            },
            {
              table: {
                widths: ['*', '*'],
                body: [
                  [{ text: 'Total Rent Collected', style: 'summaryLabel' }, { text: this.formatCurrency(this.sumField('rentPaid')), style: 'summaryValue' }],
                  [{ text: 'Total Outstanding', style: 'summaryLabel' }, { text: this.formatCurrency(this.sumField('balance')), style: 'summaryValue' }],
                  [{ text: 'Total Leases', style: 'summaryLabel' }, { text: this.previewData.length.toString(), style: 'summaryValue' }]
                ]
              },
              layout: 'noBorders'
            }
          ],
          footer: function (currentPage, pageCount) {
            return {
              stack: [
                {
                  canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#000000' }],
                  margin: [0, 0, 0, 3]
                },
                {
                  text: `Page ${currentPage} of ${pageCount}`,
                  fontSize: 7,
                  alignment: 'right',
                  margin: [0, 3, 10, 0]
                }
              ]
            };
          },
          styles: {
            header: { fontSize: 14, bold: true, color: '#000080' },
            propertyInfo: { fontSize: 8 },
            sectionHeader: { fontSize: 11, bold: true, color: '#000080', margin: [0, 0, 0, 8] },
            tableHeader: { bold: true, fontSize: 7.5, color: '#FFFFFF', alignment: 'center' },
            tableData: { fontSize: 7, alignment: 'center' },
            tableFooter: { fontSize: 7, bold: true, fillColor: '#E0E0E0', alignment: 'center' },
            summaryLabel: { fontSize: 8, bold: true },
            summaryValue: { fontSize: 8, bold: true, alignment: 'right' }
          },
          pageMargins: [30, 40, 30, 40],
          pageSize: 'A4',
          pageOrientation: 'landscape'
        };

        pdfMake.createPdf(docDefinition).open();
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.$toast.error("Failed to generate PDF report");
      } finally {
        this.isExportingPDF = false;
      }
    },

    calculateTotal(field) {
      return this.previewData.reduce((sum, item) => {
        const value = parseFloat(item[field]) || 0;
        return sum + value;
      }, 0).toFixed(2);
    }
  }
};
</script>

<style scoped>
/* Compact Card */
.compact-card {
  border-radius: 12px !important;
}

.compact-table-card {
  border-radius: 8px !important;
  border: 1px solid #e0e0e0 !important;
}

/* Property Status Table */
.property-status-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem !important;
}

.property-status-table th,
.property-status-table td {
  border: 1px solid #ddd;
  padding: 4px 6px !important;
  font-size: 0.7rem !important;
  color: #000000 !important;
}

.property-status-table th {
  background-color: #f0f0f0 !important;
  font-weight: 700 !important;
  color: #000000 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 0.6rem !important;
  border-bottom: 2px solid #ccc !important;
}

.property-status-table .totals-row {
  background-color: #e3f2fd !important;
  font-weight: bold !important;
}

.property-status-table .totals-row td {
  border-top: 2px solid #999 !important;
  font-weight: 700 !important;
}

/* Small text field */
.small-text-field :deep(.v-input__slot) {
  min-height: 36px !important;
}

.small-text-field :deep(.v-label) {
  font-size: 0.7rem !important;
  color: #000000 !important;
}

.small-text-field :deep(.v-input__control input) {
  font-size: 0.7rem !important;
  color: #000000 !important;
}

/* Caption class */
.caption {
  font-size: 0.7rem !important;
}

.text-subtitle-2 {
  font-size: 0.85rem !important;
}

/* Chips */
:deep(.v-chip) {
  font-size: 0.55rem !important;
  height: 20px !important;
}

/* Buttons */
.v-btn.caption {
  font-size: 0.65rem !important;
  letter-spacing: 0.3px;
  min-height: 30px !important;
  padding: 0 12px !important;
  text-transform: none !important;
}

.v-btn .v-icon {
  font-size: 16px !important;
}

/* Table responsive */
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Responsive */
@media (max-width: 768px) {
  .property-status-table th,
  .property-status-table td {
    font-size: 0.55rem !important;
    padding: 3px 4px !important;
  }
  
  .property-status-table th {
    font-size: 0.5rem !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>